import type { ReservationWrapper } from "@b2bportal/core-types";
import { handleAxiosError } from "@b2bportal/core-utilities";
import {
  type HomesReservation,
  type GetHomeReservationRequest,
  HomeItineraryState,
} from "@b2bportal/homes-api";
import { Product } from "@b2bportal/purchase-api";
import { homesApi } from "@overrides/utilities";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import dayjs from "dayjs";

export interface IHomesItineraryResponse {
  itineraries: {
    [key: string]: Array<ReservationWrapper<HomesReservation>>;
  };
}

export const homesPostBookingApi = createApi({
  reducerPath: "homesPostBooking",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["HomesReservationList"],
  endpoints: (builder) => ({
    fetchAllHomesReservations: builder.query<IHomesItineraryResponse, void>({
      queryFn: async () => {
        try {
          const response = await homesApi.apiV0TripsHomesGetReservationsPut({
            states: [
              HomeItineraryState.Canceled,
              HomeItineraryState.Future,
              HomeItineraryState.Present,
              HomeItineraryState.Past,
            ],
            referenceDateTime: dayjs().format(),
          });

          const wrappedReservations = Object.keys(
            response.data.itineraries
          ).reduce(
            (acc, key) => {
              const reservations = response.data.itineraries[key];
              const wrapped = reservations.map((reservation) => ({
                reservation,
                reservationId: reservation.id.value,
                sortDate: reservation.stayDates.from,
                recentFirst:
                  key === HomeItineraryState.Past ||
                  key === HomeItineraryState.Canceled,
                isCancelled: key === HomeItineraryState.Canceled,
                productType: Product.Homes,
              }));
              return { ...acc, [key]: wrapped };
            },
            {} as {
              [key: string]: Array<ReservationWrapper<HomesReservation>>;
            }
          );

          return {
            data: {
              itineraries: wrappedReservations,
            },
          };
        } catch (error) {
          return handleAxiosError(error);
        }
      },
      providesTags: ["HomesReservationList"],
    }),
    fetchHomesReservation: builder.query<
      HomesReservation,
      GetHomeReservationRequest
    >({
      queryFn: async (request) => {
        try {
          const response =
            await homesApi.apiV0TripsHomesGetReservationPut(request);

          return { data: response.data };
        } catch (error) {
          return handleAxiosError(error);
        }
      },
    }),
  }),
});
